import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  getFidesChatFirestore,
  CONVERSATIONS_COLLECTION,
} from '../../services/admin/FidesChatFirebase';

export type FidesChatLiveStatus = 'open' | 'resolved' | null;
export type FidesChatResolvedBy = 'admin' | 'investor' | null;

export interface FidesChatConversationLiveMeta {
  status: FidesChatLiveStatus;
  resolvedBy: FidesChatResolvedBy;
}

export default function useFidesChatConversationMeta(
  conversationId: string | null,
  enabled = true,
): FidesChatConversationLiveMeta {
  const [meta, setMeta] = useState<FidesChatConversationLiveMeta>({
    status: null,
    resolvedBy: null,
  });

  useEffect(() => {
    if (!enabled || !conversationId) {
      setMeta({ status: null, resolvedBy: null });
      return undefined;
    }

    let isMounted = true;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      try {
        const firestore = await getFidesChatFirestore();
        if (!isMounted) return;

        unsubscribe = onSnapshot(
          doc(firestore, CONVERSATIONS_COLLECTION, conversationId),
          (snapshot) => {
            if (!isMounted) return;
            const data = snapshot.data() as Record<string, unknown> | undefined;
            const status =
              data?.status === 'resolved' ? 'resolved' : data?.status === 'open' ? 'open' : null;
            const resolvedBy =
              data?.resolvedBy === 'investor' || data?.resolvedBy === 'admin'
                ? data.resolvedBy
                : null;
            setMeta({ status, resolvedBy });
          },
          (snapshotError) => {
            console.error('Fides chat conversation meta snapshot failed', snapshotError);
          },
        );
      } catch (firestoreError) {
        console.error('Fides chat conversation meta setup failed', firestoreError);
      }
    })();

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, [conversationId, enabled]);

  return meta;
}
